/**
 * sessionReview.js — pure helpers for SessionReviewPage state.
 *
 * Extracted as pure functions so the page's state machine can be unit
 * tested without mounting React (CLAUDE.md: no new libraries). Same
 * discipline as `apiError.js::classifyAckError` from BR-checkout-ack.
 *
 * The exported pieces:
 *   • REVIEW_STATES    — the canonical set of states (frozen object)
 *   • classifyReviewError(apiError) — maps an ApiError to a state name
 *   • subPromptFor(rating)          — returns the sub-prompt copy text
 *
 * Pure functions, no React, no DOM access. Importable from anywhere.
 */
import { ApiError } from "./apiError";

/**
 * Canonical state names for the SessionReviewPage state machine.
 *
 *   loading            — initial fetch in flight (does the user have a
 *                        reviewable booking? is it already reviewed?)
 *   idle               — form rendered, ready for rating
 *   submitting         — POST in flight
 *   success            — just submitted; thank-you screen
 *   already_submitted  — booking has an existing review
 *   too_early          — session hasn't ended yet (400 session_in_future)
 *   wrong_account      — 403 (booking belongs to a different user)
 *   session_expired    — 401 mid-flow (auth gone); auto-retry on relogin
 *   unauthenticated    — initial unauth state (sign in to continue)
 *   not_found          — 404 (booking doesn't exist or no review)
 *   error_other        — generic recoverable error
 */
export const REVIEW_STATES = Object.freeze({
  LOADING:           "loading",
  IDLE:              "idle",
  SUBMITTING:        "submitting",
  SUCCESS:           "success",
  ALREADY_SUBMITTED: "already_submitted",
  TOO_EARLY:         "too_early",
  WRONG_ACCOUNT:     "wrong_account",
  SESSION_EXPIRED:   "session_expired",
  UNAUTHENTICATED:   "unauthenticated",
  NOT_FOUND:         "not_found",
  ERROR_OTHER:       "error_other",
});

/**
 * Map an ApiError (from `apiError.js::ApiError.from`) to a review-page
 * state name. Mirrors the pattern in `classifyAckError`. Null/undefined
 * input → error_other (defensive — a missing error shouldn't crash).
 *
 * @param {ApiError | null | undefined} apiError
 * @returns {string} one of REVIEW_STATES values
 */
export function classifyReviewError(apiError) {
  if (!apiError) return REVIEW_STATES.ERROR_OTHER;

  // Status-based routing — same precedence as ApiError.kind discriminator.
  if (apiError.status === 401) return REVIEW_STATES.SESSION_EXPIRED;
  if (apiError.status === 403) return REVIEW_STATES.WRONG_ACCOUNT;
  if (apiError.status === 404) return REVIEW_STATES.NOT_FOUND;

  // 400s with a known backend code (the new POST /reviews returns these):
  //   session_in_future  → too_early
  //   already_reviewed   → already_submitted
  if (apiError.status === 400) {
    if (apiError.kind === "session_in_future")    return REVIEW_STATES.TOO_EARLY;
    if (apiError.kind === "already_reviewed")     return REVIEW_STATES.ALREADY_SUBMITTED;
  }

  return REVIEW_STATES.ERROR_OTHER;
}

/**
 * Sub-prompt copy that adapts to the user's rating. Star ratings without
 * a context-aware follow-up question feel transactional; a tailored
 * prompt ("what made it great?" vs "what could have been better?") gets
 * dramatically more meaningful free-text in practice.
 *
 * 4-5: positive ("what stood out?")
 * 3:   neutral ("what could have made it better?")
 * 1-2: negative ("what should have gone differently?")
 * null/undefined: generic ("tell us more")
 *
 * @param {number | null | undefined} rating
 * @returns {string} prompt text
 */
export function subPromptFor(rating) {
  if (rating == null) return "Tell us more (optional)";
  if (rating >= 4) return "What stood out? (optional)";
  if (rating === 3) return "What could have made it better? (optional)";
  return "What should have gone differently? (optional)";
}
